import * as colorPicker from '@zag-js/color-picker';
import { useCallback, useRef } from 'react';

export type PendingColorSource = 'area' | 'hue' | 'opacity';

interface PendingColorChange {
  color: colorPicker.Color;
  source: PendingColorSource;
}

/** Tracks an uncommitted change and flushes it when its owning control detaches. */
export function usePendingColorChange(onFlush: (color: colorPicker.Color) => void) {
  const pendingRef = useRef<PendingColorChange | null>(null);
  const onFlushRef = useRef(onFlush);

  onFlushRef.current = onFlush;

  function begin(color: colorPicker.Color, source: PendingColorSource) {
    pendingRef.current = { color, source };
  }

  function clear() {
    pendingRef.current = null;
  }

  function latest(): colorPicker.Color | undefined {
    return pendingRef.current?.color;
  }

  function hasPending(): boolean {
    return pendingRef.current !== null;
  }

  const flush = useCallback((source?: PendingColorSource) => {
    const pending = pendingRef.current;
    if (!pending || (source && pending.source !== source)) return;
    pendingRef.current = null;
    onFlushRef.current(pending.color);
  }, []);

  const rootRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node === null) flush();
    },
    [flush],
  );

  const opacityRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node === null) flush('opacity');
    },
    [flush],
  );

  return { begin, clear, hasPending, latest, opacityRef, rootRef };
}
